import type {
  GatewayBackendKind,
  PlatformProviderKeyProvider,
  ProviderProtocol,
  ThinkingLevel,
  VelugaModelCapabilities,
  VelugaModelProvider
} from '../../../shared-types/src/index.js';
import { trimTrailingSlash } from '../url.js';

export interface ProviderPreset {
  provider: PlatformProviderKeyProvider;
  catalogProvider: VelugaModelProvider;
  protocol: ProviderProtocol;
  backendKind: GatewayBackendKind;
  defaultBaseUrl: string;
  nativeAttachment?: VelugaModelCapabilities['nativeAttachment'];
  thinkingAuto?: VelugaModelCapabilities['thinkingAuto'];
  webSearchNativeProvider?: VelugaModelCapabilities['webSearchNativeProvider'];
  webSearchToolFallback?: boolean;
}

/**
 * Provider-scoped LiteLLM catch-alls for platform-admin manual models.
 * These are transport routes only: callers must still resolve an authorized
 * Veluga catalog id before the gateway can send one of these backend names.
 */
export const PROVIDER_MANUAL_WILDCARD_ROUTES: Record<
  PlatformProviderKeyProvider,
  string
> = {
  openai: 'openai/*',
  anthropic: 'anthropic/*',
  gemini: 'gemini/*',
  qwen: 'dashscope/*',
  glm: 'zai/*',
  deepseek: 'deepseek/*',
  grok: 'xai/*',
  openrouter: 'openrouter/*',
  minimax: 'minimax/*'
};

export function normalizeProviderBaseUrl(protocol: ProviderProtocol, baseUrl: string): string {
  const normalized = trimTrailingSlash(baseUrl.trim());

  try {
    const parsed = new URL(normalized);
    const path = parsed.pathname.replace(/\/+$/, '');

    if (protocol === 'gemini-compatible') {
      if (/\/v1(?:beta)?$/i.test(path)) return normalized;
      if (parsed.hostname === 'generativelanguage.googleapis.com') {
        parsed.pathname = `${path === '/' ? '' : path}/v1beta`;
        return trimTrailingSlash(parsed.toString());
      }
      return normalized;
    }

    if (/dashscope(?:-intl)?\.aliyuncs\.com$/.test(parsed.hostname) && !path.includes('/compatible-mode')) {
      parsed.pathname = '/compatible-mode/v1';
      return trimTrailingSlash(parsed.toString());
    }

    if (parsed.hostname === 'api.z.ai' && !path.startsWith('/api/paas')) {
      parsed.pathname = '/api/paas/v4';
      return trimTrailingSlash(parsed.toString());
    }

    if (parsed.hostname === 'api.x.ai' && !/\/v1$/i.test(path)) {
      parsed.pathname = `${path === '/' ? '' : path}/v1`;
      return trimTrailingSlash(parsed.toString());
    }

    if (parsed.hostname === 'openrouter.ai' && path !== '/api/v1') {
      parsed.pathname = '/api/v1';
      return trimTrailingSlash(parsed.toString());
    }
  } catch {
    // The provider probe classifies malformed or unreachable endpoints.
  }

  return normalized;
}

const unsupportedThinking: VelugaModelCapabilities['thinkingAuto'] = {
  strategy: 'unsupported',
  supportedEfforts: ['off']
};
const openaiNativeAttachment: VelugaModelCapabilities['nativeAttachment'] = {
  inlineImage: true,
  inlineDocument: true,
  providerReference: true,
  providerUpload: 'available',
  nonPdfDocumentMode: 'text_only',
  gatewayTranslationVerified: true,
  verifiedProtocols: ['openai-compatible', 'openai-responses-compatible']
};
const anthropicNativeAttachment: VelugaModelCapabilities['nativeAttachment'] = {
  inlineImage: true,
  inlineDocument: true,
  providerReference: true,
  providerUpload: 'hosting_dependent',
  nonPdfDocumentMode: 'restricted',
  gatewayTranslationVerified: true,
  verifiedProtocols: ['anthropic-messages-compatible']
};
const geminiNativeAttachment: VelugaModelCapabilities['nativeAttachment'] = {
  inlineImage: true,
  inlineDocument: true,
  providerReference: true,
  providerUpload: 'available',
  nonPdfDocumentMode: 'native',
  gatewayTranslationVerified: true,
  verifiedProtocols: ['gemini-compatible']
};

export const PROVIDER_PRESETS: Record<PlatformProviderKeyProvider, ProviderPreset> = {
  openai: {
    provider: 'openai',
    catalogProvider: 'openai',
    protocol: 'openai-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://api.openai.com/v1',
    nativeAttachment: openaiNativeAttachment,
    thinkingAuto: {
      strategy: 'openai-adaptive-default',
      supportedEfforts: ['off', 'low', 'medium', 'high', 'xhigh']
    },
    webSearchNativeProvider: 'openai'
  },
  anthropic: {
    provider: 'anthropic',
    catalogProvider: 'anthropic',
    protocol: 'anthropic-messages-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://api.anthropic.com',
    nativeAttachment: anthropicNativeAttachment,
    thinkingAuto: {
      strategy: 'anthropic-adaptive',
      supportedEfforts: ['off', 'low', 'medium', 'high', 'xhigh']
    },
    webSearchNativeProvider: 'anthropic'
  },
  gemini: {
    provider: 'gemini',
    catalogProvider: 'google',
    protocol: 'gemini-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://generativelanguage.googleapis.com/v1beta',
    nativeAttachment: geminiNativeAttachment,
    thinkingAuto: {
      strategy: 'gemini-dynamic',
      supportedEfforts: ['off', 'minimal', 'low', 'medium', 'high']
    },
    webSearchNativeProvider: 'gemini'
  },
  qwen: {
    provider: 'qwen',
    catalogProvider: 'qwen',
    protocol: 'openai-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1',
    thinkingAuto: unsupportedThinking
  },
  glm: {
    provider: 'glm',
    catalogProvider: 'glm',
    protocol: 'openai-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://api.z.ai/api/paas/v4',
    thinkingAuto: unsupportedThinking
  },
  deepseek: {
    provider: 'deepseek',
    catalogProvider: 'deepseek',
    protocol: 'openai-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://api.deepseek.com',
    thinkingAuto: {
      strategy: 'veluga-effort-map',
      supportedEfforts: ['off', 'low', 'medium', 'high', 'xhigh'],
      fallbackEffort: 'high'
    }
  },
  grok: {
    provider: 'grok',
    catalogProvider: 'grok',
    protocol: 'openai-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://api.x.ai/v1',
    thinkingAuto: {
      strategy: 'veluga-effort-map',
      supportedEfforts: ['off', 'low', 'medium', 'high'],
      fallbackEffort: 'medium'
    }
  },
  openrouter: {
    provider: 'openrouter',
    catalogProvider: 'openrouter',
    protocol: 'openai-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://openrouter.ai/api/v1',
    thinkingAuto: unsupportedThinking
  },
  minimax: {
    provider: 'minimax',
    catalogProvider: 'minimax',
    protocol: 'openai-compatible',
    backendKind: 'litellm-proxy',
    defaultBaseUrl: 'https://api.minimax.io/v1',
    thinkingAuto: unsupportedThinking,
    webSearchToolFallback: true
  }
};

export function presetFor(provider: PlatformProviderKeyProvider): ProviderPreset {
  const preset = PROVIDER_PRESETS[provider];
  if (!preset) throw new Error(`unsupported provider preset: ${provider}`);
  return preset;
}

export function presetNativeAttachmentFor(
  provider: PlatformProviderKeyProvider
): VelugaModelCapabilities['nativeAttachment'] | undefined {
  return presetFor(provider).nativeAttachment;
}

export function presetThinkingAutoFor(
  provider: PlatformProviderKeyProvider
): VelugaModelCapabilities['thinkingAuto'] | undefined {
  return presetFor(provider).thinkingAuto;
}

export function thinkingLevelsFor(capabilities: VelugaModelCapabilities): ThinkingLevel[] {
  const auto = capabilities.thinkingAuto;
  const manual = auto?.supportedEfforts ?? ['off'];
  const levels: ThinkingLevel[] = ['off'];
  if (auto && auto.strategy !== 'unsupported') levels.push('auto');
  for (const level of manual) {
    if (!levels.includes(level)) levels.push(level);
  }
  return levels;
}
